import { Link } from "react-router-dom";
import { resolveImg, inr, num, imgFallback } from "@/lib/api";
import { lotCode, brandTypeClass } from "@/lib/config";
import { MapPin, ImageSquare, Play, Table, Package } from "@phosphor-icons/react";

export function LotCard({ lot, index = 0 }) {
  const img = resolveImg(lot.image_ids?.[0]);
  const brands = (lot.brand || "").split(",").map((b) => b.trim()).filter(Boolean).join(" • ") || lot.category;
  const discount = lot.mrp_value ? Math.round((1 - lot.asking_price / lot.mrp_value) * 100) : 0;
  const photos = lot.image_ids?.length || 0;
  const code = lotCode(lot);

  return (
    <div className="h-full">
      <Link
        to={`/lots/${lot.id}`}
        data-testid={`lot-card-${lot.id}`}
        className="group flex h-full flex-col overflow-hidden rounded-xl border border-border bg-white transition-shadow duration-200 hover:shadow-[0_12px_30px_-12px_rgba(17,24,39,0.18)]"
      >
        <div className="relative aspect-[16/9] overflow-hidden bg-navy-50">
          {img ? (
            <img src={img} onError={imgFallback} alt={lot.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          ) : (
            <div className="flex h-full items-center justify-center"><Package size={40} className="text-ink/15" /></div>
          )}
          <span className="absolute left-3 top-3 rounded-md bg-white/95 px-2 py-1 text-[11px] font-semibold tracking-tight text-subtext shadow-sm">LOT {code}</span>
          <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-md bg-ok px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
            <span className="h-1.5 w-1.5 rounded-full bg-white" /> Live
          </span>
        </div>

        <div className="flex flex-1 flex-col p-5">
          <h3 className="text-lg font-bold leading-tight text-ink">{brands}</h3>
          <p className="mt-0.5 text-sm text-subtext line-clamp-1">{lot.title}</p>

          {(lot.vehicle_type || lot.brand_type) && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {lot.vehicle_type && <span className="rounded-md bg-navy/5 px-2 py-0.5 text-[11px] font-semibold text-navy" data-testid={`lot-vehicle-type-${lot.id}`}>{lot.vehicle_type}</span>}
              {lot.brand_type && <span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${brandTypeClass(lot.brand_type)}`} data-testid={`lot-brand-type-${lot.id}`}>{lot.brand_type}</span>}
            </div>
          )}

          <div className="mt-4 flex items-end justify-between rounded-lg border border-border bg-navy-50 px-4 py-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-subtext">MRP Value</div>
              <div className="stat text-sm font-semibold text-subtext line-through decoration-subtext/40">{inr(lot.mrp_value)}</div>
              <div className="mt-1 text-[11px] uppercase tracking-wider text-subtext">Asking Price</div>
              <div className="stat text-xl font-bold text-ink">{inr(lot.asking_price)}</div>
            </div>
            {discount > 0 && (
              <span className="stat rounded-md bg-brand px-2.5 py-1 text-sm font-bold text-white">{discount}% OFF</span>
            )}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border">
            <Stat value={num(lot.unique_skus)} label="SKUs" />
            <Stat value={num(lot.total_units)} label="Parts" />
            <Stat value={lot.state || lot.city} label="Location" small />
            <Stat value={lot.condition} label="Parts Condition" small />
          </div>

          <div className="mt-3">
            <span className="inline-block rounded-md bg-palered px-2.5 py-1 text-xs font-medium text-brand">Dead / Slow-Moving Stock</span>
          </div>

          <div className="mt-auto flex items-center gap-4 border-t border-border pt-4 text-xs text-subtext">
            <span className="flex items-center gap-1"><ImageSquare size={14} /> {photos} Photos</span>
            <span className="flex items-center gap-1"><Play size={14} weight="fill" /> Video</span>
            <span className="flex items-center gap-1"><Table size={14} /> Excel</span>
          </div>

          <div className="mt-4 w-full rounded-lg bg-brand py-2.5 text-center text-sm font-semibold text-white transition-colors group-hover:bg-brand-hover">
            View Full Lot
          </div>
        </div>
      </Link>
    </div>
  );
}

function Stat({ value, label, small }) {
  return (
    <div className="bg-white p-3">
      <div className={`stat font-bold text-ink ${small ? "text-sm" : "text-lg"}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-subtext">{label}</div>
    </div>
  );
}
